import "./LeagueHubCard.css";

import {
  fetchLeagueResource,
  type SportsLeagueRecord,
  type SportsProviderResourceResponse,
} from "../../api/sports";
import { renderLeagueScheduleCard } from "./LeagueScheduleCard";

const LABELS: Record<string, string> = {
  overview: "Overview",
  matches: "Matches",
  games: "Games",
  standings: "Standings",
  conferences: "Conferences",
  teams: "Teams",
  players: "Players",
  fighters: "Fighters",
  leagues: "Leagues",
  tournaments: "Tournaments",
  rankings: "Rankings",
  stadiums: "Stadiums",
  events: "Events",
  drivers: "Drivers",
  circuits: "Circuits",
  courses: "Courses",
  regions: "Regions",
  champions: "Champions",
  heroes: "Heroes",
  bracket: "Bracket",
  stats: "Stats",
};

function esc(value: unknown): string {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function title(value: string): string {
  return LABELS[value] || value.replace(/_/g, " ").replace(/\b\w/g, (x) => x.toUpperCase());
}

function seasonLabel(league: SportsLeagueRecord): string {
  const season = league.current_season;
  if (!season) return "";
  if (league.slug === "fifa-world-cup") return `${season} Edition`;
  return `${season} Season`;
}

function rowName(row: any): string {
  const nested = row?.team || row?.player || row?.fighter || row?.driver || row?.course || row?.circuit || row?.league;
  const source = nested && typeof nested === "object" ? nested : row;
  return String(
    source?.display_name || source?.full_name || source?.localized_name || source?.name ||
    source?.short_name || source?.nickname ||
    [source?.first_name, source?.last_name].filter(Boolean).join(" ") || "Item"
  );
}

function rowSub(row: any): string {
  const nested = row?.team || row?.player || row?.fighter || row?.driver || row?.course || row?.circuit || row?.league;
  const source = nested && typeof nested === "object" ? nested : row;
  const bits = [
    source?.abbreviation || source?.acronym || source?.short_name,
    source?.location || source?.city || source?.country || source?.country_name || source?.nationality,
    source?.conference?.name || source?.conference_name,
    source?.division_name,
    source?.position,
    source?.weight_class?.name,
  ].filter(Boolean);
  return bits.slice(0, 3).map(String).join(" · ");
}

function teamLogo(row: any): string {
  const team = row?.team && typeof row.team === "object" ? row.team : row;
  return String(team?.icon_url || "");
}

function renderStandings(rows: any[]): string {
  if (!rows.length) return renderEmpty("No standings are cached yet.");
  return `<div class="sports-hub-table-wrap"><table class="sports-hub-table">
    <thead><tr><th>#</th><th>Team</th><th>GP</th><th>W</th><th>L</th><th>D/T</th><th>Pct/Pts</th></tr></thead>
    <tbody>${rows.slice(0, 60).map((row: any, i) => {
      const logo = teamLogo(row);
      const gp = row.games_played ?? ((Number(row.wins || 0) + Number(row.losses || 0) + Number(row.ties || row.draws || 0)) || "—");
      const pct = row.win_percentage ?? row.win_percent ?? row.points ?? "—";
      return `<tr>
      <td>${esc(row.playoff_seed ?? row.conference_rank ?? row.rank ?? row.position ?? i + 1)}</td>
      <td><div class="sports-hub-team-cell">${logo ? `<img src="${esc(logo)}" alt="" loading="lazy" />` : ""}<div><strong>${esc(rowName(row))}</strong>${(row.division_name || row.conference || row.group_name || row.group) ? `<small>${esc(row.division_name || row.conference || row.group_name || row.group)}</small>` : ""}</div></div></td>
      <td>${esc(gp)}</td>
      <td>${esc(row.wins ?? "—")}</td>
      <td>${esc(row.losses ?? "—")}</td>
      <td>${esc(row.draws ?? row.ties ?? "—")}</td>
      <td>${esc(pct)}</td>
    </tr>`;
    }).join("")}</tbody>
  </table></div>`;
}

function renderRankings(rows: any[]): string {
  if (!rows.length) return renderEmpty("No rankings are cached yet.");
  return `<div class="sports-hub-list">${rows.slice(0, 30).map((row: any, i) => `
    <div class="sports-hub-list-row">
      <span class="sports-hub-rank">${esc(row.rank ?? row.position ?? i + 1)}</span>
      <div><strong>${esc(rowName(row))}</strong><small>${esc(rowSub(row))}</small></div>
      ${row.points != null ? `<span class="sports-hub-value">${esc(row.points)}</span>` : ""}
    </div>`).join("")}</div>`;
}

function formatEventDate(row: any): string {
  const raw = row.start_date || row.start_at || row.date || row.begin_at || row.main_card_start_time;
  if (!raw) return "";
  const dt = new Date(raw);
  if (Number.isNaN(dt.getTime())) return String(raw);
  return new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric", year: "numeric" }).format(dt);
}

function renderEvents(rows: any[]): string {
  if (!rows.length) return renderEmpty("No events are cached yet.");
  return `<div class="sports-hub-cards">${rows.slice(0, 18).map((row: any) => {
    const venue = row.venue_name || row.location || row.city || row.course?.name || row.circuit?.name || "";
    const status = row.status || row.status_state || "";
    return `<article class="sports-hub-item-card"><strong>${esc(rowName(row))}</strong>
      <span>${esc([formatEventDate(row), status].filter(Boolean).join(" · "))}</span>
      ${venue ? `<small>${esc(venue)}</small>` : ""}</article>`;
  }).join("")}</div>`;
}

function renderGeneric(rows: any[]): string {
  if (!rows.length) return renderEmpty("No cached data is available yet.");
  return `<div class="sports-hub-cards">${rows.slice(0, 30).map((row: any) => {
    const extra = rowSub(row);
    const team = row?.team || row?.player?.team || null;
    const logo = String(team?.icon_url || "");
    return `<article class="sports-hub-item-card sports-hub-item-card-with-logo">${logo ? `<img src="${esc(logo)}" alt="" loading="lazy" />` : ""}<div><strong>${esc(rowName(row))}</strong>${extra ? `<small>${esc(extra)}</small>` : ""}</div></article>`;
  }).join("")}</div>`;
}

function renderMatches(rows: any[]): string {
  if (!rows.length) return renderEmpty("No World Cup matches are cached yet.");
  const grouped = new Map<string, any[]>();
  rows.forEach((row: any) => {
    const stage = String(row?.stage || "Matches");
    const items = grouped.get(stage) || [];
    items.push(row);
    grouped.set(stage, items);
  });
  return `<div class="sports-hub-worldcup-matches">${Array.from(grouped.entries()).map(([stage, games]) => `
    <section class="sports-hub-match-stage"><h3>${esc(stage)}</h3><div class="sports-hub-match-list">${games.map((game: any) => {
      const home = game?.home_team || {};
      const away = game?.away_team || {};
      const when = game?.date ? overviewGameDate(String(game.date)) : { date: "TBD", time: "" };
      return `<article class="sports-hub-match-row">
        <div class="sports-hub-match-teams">
          <div>${home.icon_url ? `<img src="${esc(home.icon_url)}" alt="" loading="lazy" />` : ""}<strong>${esc(home.name || "TBD")}</strong><span>${esc(home.score ?? "—")}</span></div>
          <div>${away.icon_url ? `<img src="${esc(away.icon_url)}" alt="" loading="lazy" />` : ""}<strong>${esc(away.name || "TBD")}</strong><span>${esc(away.score ?? "—")}</span></div>
        </div>
        <div class="sports-hub-match-when"><strong>${esc(when.date)}</strong><span>${esc(game?.status_state === "final" ? "Final" : (game?.status || when.time))}</span></div>
      </article>`;
    }).join("")}</div></section>`).join("")}</div>`;
}

function renderBracket(rows: any[]): string {
  if (!rows.length) return renderEmpty("No World Cup knockout bracket is cached yet.");
  const grouped = new Map<string, any[]>();
  rows.forEach((row: any) => {
    const stage = String(row?.stage || "Knockout");
    const items = grouped.get(stage) || [];
    items.push(row);
    grouped.set(stage, items);
  });
  return `<div class="sports-hub-bracket">${Array.from(grouped.entries()).map(([stage, games]) => `
    <section class="sports-hub-bracket-round">
      <h3>${esc(stage)}</h3>
      <div class="sports-hub-bracket-games">${games.map((game: any) => {
        const home = game?.home_team || {};
        const away = game?.away_team || {};
        const when = game?.date ? overviewGameDate(String(game.date)).date : "";
        return `<article class="sports-hub-bracket-game">
          <div class="sports-hub-bracket-team">${home.icon_url ? `<img src="${esc(home.icon_url)}" alt="" loading="lazy" />` : ""}<strong>${esc(home.name || "TBD")}</strong><span>${esc(home.score ?? "—")}</span></div>
          <div class="sports-hub-bracket-team">${away.icon_url ? `<img src="${esc(away.icon_url)}" alt="" loading="lazy" />` : ""}<strong>${esc(away.name || "TBD")}</strong><span>${esc(away.score ?? "—")}</span></div>
          <small>${esc([when, game?.status].filter(Boolean).join(" · "))}</small>
        </article>`;
      }).join("")}</div>
    </section>`).join("")}</div>`;
}

function renderStats(rows: any[]): string {
  if (!rows.length) return renderEmpty("No World Cup player statistics are cached yet.");
  const scorers = rows.filter((row: any) => row?.metric === "goals").slice(0, 10);
  const assists = rows.filter((row: any) => row?.metric === "assists").slice(0, 10);
  const section = (label: string, items: any[], suffix: string) => `<section class="sports-hub-stats-section"><h3>${esc(label)}</h3><div class="sports-hub-list">${items.map((row: any, i: number) => {
    const player = row?.player || {};
    return `<div class="sports-hub-list-row sports-hub-player-stat-row">
      <span class="sports-hub-rank">${i + 1}</span>
      <div><strong>${esc(player.name || "Player")}</strong><small>${esc(player.nationality || player?.team?.name || "")}</small></div>
      <span class="sports-hub-value">${esc(row?.value ?? "—")} ${esc(suffix)}</span>
    </div>`;
  }).join("")}</div></section>`;
  return `<div class="sports-hub-stats-grid">${section("Top scorers", scorers, "goals")}${section("Top assists", assists, "assists")}</div>`;
}

function renderPlayers(rows: any[]): string {
  if (!rows.length) return renderEmpty("No World Cup players are cached yet.");
  return `<div class="sports-hub-player-grid">${rows.map((row: any) => {
    const player = row?.player || {};
    const subtitle = [player.position, player.nationality, row?.goals != null ? `${row.goals} goals` : ""].filter(Boolean).join(" · ");
    return `<article class="sports-hub-player-card">
      ${player.photo ? `<img src="${esc(player.photo)}" alt="" loading="lazy" referrerpolicy="no-referrer" />` : `<span class="sports-hub-player-fallback">${esc(String(player.name || "P").slice(0, 1))}</span>`}
      <div><strong>${esc(player.name || "Player")}</strong>${subtitle ? `<small>${esc(subtitle)}</small>` : ""}</div>
    </article>`;
  }).join("")}</div>`;
}

function renderEmpty(message: string): string {
  return `<div class="sports-hub-empty">${esc(message)}</div>`;
}

function renderResource(kind: string, response: SportsProviderResourceResponse): string {
  const rows = Array.isArray(response.payload?.data) ? response.payload!.data as any[] : [];
  if (kind === "standings") return renderStandings(rows);
  if (kind === "rankings") return renderRankings(rows);
  if (kind === "events") return renderEvents(rows);
  if (kind === "matches") return renderMatches(rows);
  if (kind === "bracket") return renderBracket(rows);
  if (kind === "stats") return renderStats(rows);
  if (kind === "players") return renderPlayers(rows);
  return renderGeneric(rows);
}

function overviewGameDate(startsAt: string | null): { date: string; time: string } {
  if (!startsAt) return { date: "TBD", time: "" };
  const dt = new Date(startsAt);
  if (Number.isNaN(dt.getTime())) return { date: String(startsAt), time: "" };
  return {
    date: new Intl.DateTimeFormat(undefined, { weekday: "short", month: "short", day: "numeric" }).format(dt),
    time: new Intl.DateTimeFormat(undefined, { hour: "numeric", minute: "2-digit" }).format(dt),
  };
}

function renderOverviewTeam(team: any): string {
  const name = String(team?.name || "TBD");
  const logo = String(team?.icon_url || "");
  const score = team?.score;
  return `<div class="sports-hub-overview-team">
    ${logo ? `<img src="${esc(logo)}" alt="" loading="lazy" />` : `<span class="sports-hub-overview-team-fallback">${esc(String(team?.abbreviation || name).slice(0, 3).toUpperCase())}</span>`}
    <strong>${esc(name)}</strong>
    ${score !== null && score !== undefined ? `<span class="sports-hub-overview-score">${esc(score)}</span>` : ""}
  </div>`;
}

function renderOverview(league: SportsLeagueRecord): string {
  const games = Array.isArray(league.schedule?.games) ? league.schedule!.games.filter((game) => game.display_type !== "event").slice(0, 5) : [];
  if (games.length) {
    return `<div class="sports-hub-overview sports-hub-core-overview">
      <div class="sports-hub-overview-heading">
        <h3>${esc(league.short_name || league.name)} games</h3>
        <button type="button" data-sports-hub-jump="${league.slug === "fifa-world-cup" ? "matches" : "games"}">View all games</button>
      </div>
      <div class="sports-hub-overview-games">
        ${games.map((game) => {
          const when = overviewGameDate(game.starts_at);
          const state = String(game.status_state || "").toLowerCase();
          const status = state === "in_progress" ? "Live" : state === "final" ? "Final" : when.time;
          return `<article class="sports-hub-overview-game" data-overview-game-id="${esc(game.id)}">
            <div class="sports-hub-overview-teams">
              ${renderOverviewTeam(game.away_team)}
              ${renderOverviewTeam(game.home_team)}
            </div>
            <div class="sports-hub-overview-game-divider" aria-hidden="true"></div>
            <div class="sports-hub-overview-when">
              <strong>${esc(when.date)}</strong>
              <span>${esc(status)}</span>
            </div>
          </article>`;
        }).join("")}
      </div>
    </div>`;
  }

  const capabilities = (league.free_capabilities || []).map(title);
  return `<div class="sports-hub-overview sports-hub-core-overview sports-hub-overview-no-games">
    <h3>${esc(league.short_name || league.name)} overview</h3>
    <p>No cached game rows are available for this league's Overview right now. Use the tabs above for the free resources currently configured for AyeSearch.</p>
    <div class="sports-hub-capabilities">${capabilities.map((item) => `<span>${esc(item)}</span>`).join("")}</div>
  </div>`;
}

function renderHeader(league: SportsLeagueRecord): string {
  return `<header class="sports-hub-header">
    <div><div class="sports-hub-kicker">AYESEARCH SPORTS</div><h1>${esc(league.short_name || league.name)}</h1><p>${esc(seasonLabel(league))}</p></div>
    <div class="sports-hub-sport-label">${esc(league.sport.common_name)}</div>
  </header>`;
}

function tabButton(tab: string, active: string): string {
  return `<button type="button" class="sports-hub-tab${tab === active ? " sports-hub-tab-active" : ""}" data-sports-hub-tab="${esc(tab)}">${esc(title(tab))}</button>`;
}

export function renderLeagueHubCard(league: SportsLeagueRecord): string {
  const hub = league.hub;
  const tabs = hub?.tabs?.length ? hub.tabs : ["overview", "matches"];
  const active = hub?.default_resource || "overview";
  return `<article class="sports-hub-card" data-sports-hub="${esc(league.slug)}">
    ${renderHeader(league)}
    <nav class="sports-hub-tabs" aria-label="Sports sections">${tabs.map((tab) => tabButton(tab, active)).join("")}</nav>
    <section class="sports-hub-panel" data-sports-hub-panel>
      ${active === "overview" ? renderOverview(league) : `<div class="sports-hub-loading">Loading ${esc(title(active))}…</div>`}
    </section>
    <footer class="sports-hub-footer">Free-tier sports data is cached by AyeSearch. Provider schedule access remains separately configured.</footer>
  </article>`;
}

async function loadResource(root: HTMLElement, league: SportsLeagueRecord, resource: string, params: Record<string, string | number> = {}): Promise<void> {
  const panel = root.querySelector<HTMLElement>("[data-sports-hub-panel]");
  if (!panel) return;
  const meta = league.hub?.free_resources?.[resource];
  if (!meta) {
    panel.innerHTML = renderEmpty(`${title(resource)} is not available on the configured free tier.`);
    return;
  }
  panel.innerHTML = `<div class="sports-hub-loading">Loading ${esc(meta.label)}…</div>`;
  try {
    const response = await fetchLeagueResource(league.slug, resource, params);
    panel.innerHTML = renderResource(meta.kind || resource, response);
  } catch (error) {
    panel.innerHTML = renderEmpty(error instanceof Error ? error.message : "Unable to load this sports resource.");
  }
}

async function loadConferenceSelector(root: HTMLElement, league: SportsLeagueRecord): Promise<void> {
  const panel = root.querySelector<HTMLElement>("[data-sports-hub-panel]");
  if (!panel) return;
  panel.innerHTML = `<div class="sports-hub-loading">Loading conferences…</div>`;
  try {
    const response = await fetchLeagueResource(league.slug, "conferences");
    const rows = Array.isArray(response.payload?.data) ? response.payload!.data as any[] : [];
    panel.innerHTML = `<div class="sports-hub-picker"><p>Select a conference to view standings.</p><div class="sports-hub-picker-buttons">${rows.slice(0, 40).map((row: any) => `<button type="button" data-conference-id="${esc(row.id)}">${esc(row.short_name || row.abbreviation || row.name || `Conference ${row.id}`)}</button>`).join("")}</div></div>`;
    panel.querySelectorAll<HTMLButtonElement>("[data-conference-id]").forEach((button) => {
      button.addEventListener("click", () => {
        void loadResource(root, league, "standings", { conference_id: button.dataset.conferenceId || "" });
      });
    });
  } catch (error) {
    panel.innerHTML = renderEmpty(error instanceof Error ? error.message : "Unable to load conferences.");
  }
}

async function activate(root: HTMLElement, league: SportsLeagueRecord, tab: string): Promise<void> {
  root.querySelectorAll<HTMLElement>("[data-sports-hub-tab]").forEach((button) => {
    button.classList.toggle("sports-hub-tab-active", button.dataset.sportsHubTab === tab);
  });
  const panel = root.querySelector<HTMLElement>("[data-sports-hub-panel]");
  if (!panel) return;
  if (tab === "overview") {
    panel.innerHTML = renderOverview(league);
    return;
  }
  if (tab === "matches" && league.slug === "fifa-world-cup") {
    await loadResource(root, league, "matches");
    return;
  }
  if (tab === "matches" || tab === "games") {
    const wrapper = document.createElement("div");
    wrapper.innerHTML = renderLeagueScheduleCard(league);
    const scheduleCard = wrapper.firstElementChild as HTMLElement | null;
    if (scheduleCard) {
      scheduleCard.classList.add("sports-hub-embedded-schedule");
      panel.innerHTML = "";
      panel.appendChild(scheduleCard);
    } else {
      panel.innerHTML = renderEmpty("No schedule view is configured.");
    }
    return;
  }
  const resource = league.hub?.free_resources?.[tab];
  if (resource?.requires?.includes("conference_id")) {
    await loadConferenceSelector(root, league);
    return;
  }
  await loadResource(root, league, tab);
}

export function bindLeagueHubCard(league: SportsLeagueRecord): void {
  const root = document.querySelector<HTMLElement>(`[data-sports-hub="${CSS.escape(league.slug)}"]`);
  if (!root) return;
  root.querySelectorAll<HTMLButtonElement>("[data-sports-hub-tab]").forEach((button) => {
    button.addEventListener("click", () => {
      const tab = button.dataset.sportsHubTab || "overview";
      void activate(root, league, tab);
    });
  });
  root.addEventListener("click", (event) => {
    const target = (event.target as HTMLElement | null)?.closest<HTMLElement>("[data-sports-hub-jump]");
    if (!target || !root.contains(target)) return;
    const tab = target.dataset.sportsHubJump || "overview";
    void activate(root, league, tab);
  });
  void activate(root, league, league.hub?.default_resource || "overview");
}
